import { defineMessages } from '@/components/formatted_message/FormattedMessage';

import {
  AUDIT_LOG_ACTION_TRANSLATED,
  COLLECTION_TRANSLATED,
  COMMODITY_TRANSLATED,
  GRANULARITY_TRANSLATED,
  PERIOD_TRANSLATED,
  PREVIOUS_TRANSLATED,
  SOURCE_TRANSLATED,
} from '@/common/messages/entities';
import { USER_ROLES_TRANSLATED } from '@/common/messages/user/role';
import { Granularity as GranularityEnum } from '@enmon/commons/lib/enmon';
import { METER_PLACEMENT_TRANSLATED } from '@/common/messages/meter/placement';
import { METER_KIND_TRANSLATED } from '@/common/messages/meter/kind';
import { METER_STATUS_CODE_TRANSLATED } from '@/common/messages/meter/statistics/status';
import { APP_DICTIONARY } from '@/common/messages/root';
import { ThirdPartyType } from '@enmon/commons/lib/tools';
import { meterDetailFieldsTranslated } from '@/common/messages/meter/fields';
import { SUPPLY_POINT_CATEGORY_TRANSLATED } from '@/common/messages/supply_point';

export const Filter = defineMessages({
  filter: { id: 'app.components.Filter.title' },
  isActive: { id: 'app.components.Filter.isActive' },
  noFilter: { id: 'app.components.Filter.noFilter' },
  hide: { id: 'app.components.Filter.hide' },
  reset: { id: 'app.components.Filter.reset' },
  showFilter: { id: 'app.components.Filter.showFilter' },
  chosen: { id: 'app.components.Filter.chosen' },
});

export const Establishments = defineMessages({
  placeholder: { id: 'app.components.Filter.Establishments.placeholder' },
  label: { id: 'app.components.Filter.Establishments.label' },
  default: { id: 'app.components.TreeSelect.Establishments.default' },
});

export const SupplyPoints = defineMessages({
  placeholder: { id: 'app.components.Filter.SupplyPoints.placeholder' },
  label: APP_DICTIONARY.supplyPoints,
});

export const Granularity = defineMessages({
  label: { id: 'app.components.Filter.Granularity.label' },
  placeholder: { id: 'app.components.Filter.Granularity.placeholder' },
  minute: { id: 'app.components.Filter.Granularity.minute' },
  ...GRANULARITY_TRANSLATED,
});

export const Source = defineMessages({
  label: { id: 'app.components.Filter.Sources.label' },
  placeholder: { id: 'app.components.Filter.Sources.placeholder' },
  ...SOURCE_TRANSLATED,
});

export const Period = defineMessages({
  label: { id: 'app.components.Filter.Period.label' },
  placeholder: { id: 'app.components.Filter.Period.placeholder' },
  from: { id: 'app.components.Filter.Period.from' },
  to: { id: 'app.components.Filter.Period.to' },
  ...PERIOD_TRANSLATED,
});

export const Previous = defineMessages({
  label: { id: 'app.components.Filter.Previous.label' },
  placeholder: { id: 'app.components.Filter.Previous.placeholder' },
  none: { id: 'app.components.Filter.Previous.none' },
  exactPreviousDateStart: APP_DICTIONARY.date,
  ...PREVIOUS_TRANSLATED,
});

export const Search = defineMessages({
  title: { id: 'app.components.Filter.Search.title' },
  placeholder: { id: 'app.components.Filter.Search.placeholder' },
  label: { id: 'app.components.Filter.Search.label' },
});

export const Tags = defineMessages({
  establishmentsWithTag: { id: 'app.components.Filter.Tags.establishmentsWithTag' },
  placeholder: { id: 'app.components.Filter.Tags.placeholder' },
  label: APP_DICTIONARY.tags,
});

export const IsCorrection = defineMessages({
  label: { id: 'app.components.Filter.IsCorrection.label' },
  placeholder: { id: 'app.components.Filter.IsCorrection.placeholder' },
  false: APP_DICTIONARY.regularInvoice,
  true: APP_DICTIONARY.correctionInvoice,
});

export const ManualRecordStatuses = defineMessages({
  label: { id: 'app.components.Filter.ManualRecordStatuses.label' },
  placeholder: { id: 'app.components.Filter.ManualRecordStatuses.placeholder' },
});

export const BillingStatuses = defineMessages({
  label: { id: 'app.components.Filter.BillingStatuses.label' },
  placeholder: { id: 'app.components.Filter.BillingStatuses.placeholder' },
});

export const RemoteReadingType = defineMessages({
  label: { id: 'app.components.Filter.RemoteReadingType.label' },
  placeholder: { id: 'app.components.Filter.RemoteReadingType.placeholder' },
});

export const Grouping = defineMessages({
  [GranularityEnum.Day]: { id: 'app.containers.Analysis.Consumption.DailyUsage.grouping.day' },
  [GranularityEnum.Week]: { id: 'app.containers.Analysis.Consumption.DailyUsage.grouping.week' },
  [GranularityEnum.Year]: { id: 'app.containers.Analysis.Consumption.DailyUsage.grouping.year' },
  placeholder: { id: 'app.components.Filter.Grouping.placeholder' },
  invoice: APP_DICTIONARY.invoice,
  manual: { id: 'app.components.Filter.Grouping.manual' },
  sensor: { id: 'app.components.Filter.Grouping.sensor' },
});

export const UserRole = defineMessages({
  userRole: { id: 'app.components.Filter.UserRole.userRole' },
  any: { id: 'app.components.Filter.UserRole.any' },
  ...USER_ROLES_TRANSLATED,
});

export const EmissionFactors = defineMessages({
  placeholder: { id: 'app.components.Filter.EmissionFactors.placeholder' },
  label: APP_DICTIONARY.emissionFactors,
});

export const EmissionFactorCategories = defineMessages({
  placeholder: { id: 'app.components.Filter.EmissionFactorCategories.placeholder' },
  label: { id: 'app.components.Filter.EmissionFactorCategories.label' },
});

export const Meters = defineMessages({
  placeholder: { id: 'app.components.Filter.Meters.placeholder' },
  label: APP_DICTIONARY.meters,
  serialShort: { id: 'app.components.select.meters.serialShort' },
});

export const MeterRegisters = defineMessages({
  label: APP_DICTIONARY.registers,
  placeholder: { id: 'app.components.Filter.MeterRegisters.placeholder' },
  optionLabel: { id: 'app.components.Filter.MeterRegisters.optionLabel' },
});

export const PMax = defineMessages({
  placeholder: { id: 'app.components.select.PMax.placeholder' },
  label: meterDetailFieldsTranslated.pMax,
});

export const QMin = defineMessages({
  placeholder: { id: 'app.components.select.QMin.placeholder' },
  label: meterDetailFieldsTranslated.qMin,
});

export const QMax = defineMessages({
  placeholder: { id: 'app.components.select.QMax.placeholder' },
  label: meterDetailFieldsTranslated.qMax,
});

export const LayLength = defineMessages({
  placeholder: { id: 'app.components.select.LayLength.placeholder' },
  label: meterDetailFieldsTranslated.layLength,
});

export const MeterPlacement = defineMessages({
  placeholder: { id: 'app.components.Filter.MeterPlacements.placeholder' },
  label: { id: 'app.components.Filter.MeterPlacements.label' },
  ...METER_PLACEMENT_TRANSLATED,
});

export const MeterKind = defineMessages({
  placeholder: { id: 'app.components.Filter.MeterKinds.placeholder' },
  label: { id: 'app.components.Filter.MeterKinds.label' },
  ...METER_KIND_TRANSLATED,
});

export const MeterStatus = defineMessages({
  placeholder: { id: 'app.components.Filter.MeterStatuses.placeholder' },
  label: { id: 'app.components.Filter.MeterStatuses.label' },
  ...METER_STATUS_CODE_TRANSLATED,
});

export const Commodity = defineMessages({
  label: APP_DICTIONARY.commodity,
  placeholder: { id: 'app.components.Filter.Commodities.placeholder' },
  ...COMMODITY_TRANSLATED,
});

export const SupplyPointStatuses = defineMessages({
  label: { id: 'app.components.Filter.SupplyPointStatuses.label' },
});

export const Severity = defineMessages({
  label: {
    id: 'app.common.notifications.field.severity',
  },
});

export const NotificationType = defineMessages({
  label: {
    id: 'app.common.notifications.field.type',
  },
});

export const SupplyPointCategory = defineMessages({
  label: APP_DICTIONARY.category,
  placeholder: { id: 'app.components.Filter.SupplyPointCategory.placeholder' },
  ...SUPPLY_POINT_CATEGORY_TRANSLATED,
});

export const Customers = defineMessages({
  label: APP_DICTIONARY.customers,
  placeholder: { id: 'app.components.Filter.Customers.placeholder' },
});

export const Action = defineMessages({
  label: { id: 'app.components.Filter.Actions.label' },
  placeholder: { id: 'app.components.Filter.Actions.placeholder' },
  ...AUDIT_LOG_ACTION_TRANSLATED,
});

export const Collection = defineMessages({
  label: { id: 'app.components.Filter.Collection.label' },
  placeholder: { id: 'app.components.Filter.Collection.placeholder' },
  ...COLLECTION_TRANSLATED,
});

export const Date = defineMessages({
  placeholder: { id: 'app.components.Filter.Date.placeholder' },
  label: APP_DICTIONARY.date,
});

export const ScopeAndStream = defineMessages({
  label: { id: 'app.components.select.scopeAndStream' },
});

export const LeaseUnits = defineMessages({
  placeholder: { id: 'app.components.Filter.LeaseUnits.placeholder' },
  label: { id: 'app.components.Filter.LeaseUnits.label' },
});

export const InvoiceDetailType = defineMessages({
  placeholder: { id: 'app.components.Filter.InvoiceDetailType.placeholder' },
});

export const FloorPlans = defineMessages({
  placeholder: { id: 'app.components.Filter.FloorPlan.placeholder' },
  label: { id: 'app.components.Filter.FloorPlan.label' },
});

export const ThirdParties = defineMessages({
  [ThirdPartyType.Supplier]: { id: 'app.components.Filter.ThirdParties.supplier.placeholder' },
  [ThirdPartyType.Distributor]: { id: 'app.components.Filter.ThirdParties.distributor.placeholder' },
  label: { id: 'app.components.Filter.ThirdParties.company' },
  placeholder: { id: 'app.components.Filter.ThirdParties.company.placeholder' },
  unknownCompany: { id: 'app.components.Filter.ThirdParties.unknownCompany' },
});

export const Manufacturer = defineMessages({
  placeholder: { id: 'app.components.Filter.Manufacturer.placeholder' },
  askNewManufacturer: { id: 'app.components.select.Manufacturer.askNewManufacturer' },
});

export const MeterModel = defineMessages({
  placeholder: { id: 'app.components.Filter.MeterModel.placeholder' },
  askNewModel: { id: 'app.components.select.MeterModel.askNewModel' },
  familyHint: { id: 'app.components.select.MeterModel.familyHint' },
});
